// BHP-1070: onboarding customer-create form. Collects the required
// CreateCustomerDto fields, validates client-side, submits via useCreateCustomer
// and shows server field errors (problem+json) while preserving input.
// Styled with the app's Bootstrap 5 form classes (Tailwind migration = BHP-1038).
import React, { useState } from 'react';
import { toFormErrors } from '../../../shared/http/errorMessage';
import {
  CUSTOMER_TYPES,
  type CustomerType,
  type NewCustomer
} from '../domain/customer';
import { useCreateCustomer } from '../application/customerMutations';

interface Props {
  /** Called after the customer is created (the gate then re-routes to the app). */
  onCreated?: () => void;
}

interface FormState {
  phoneNumber: string;
  email: string;
  administrationFirstName: string;
  administrationLastName: string;
  companyName: string;
  type: CustomerType;
}

const EMPTY_FORM: FormState = {
  phoneNumber: '',
  email: '',
  administrationFirstName: '',
  administrationLastName: '',
  companyName: '',
  type: 'customer'
};

/**
 * BHP-1048: inputs that can actually display a message. `type` is deliberately
 * absent — it is a select of valid choices with no error slot, so binding a
 * violation to it would hide the message instead of showing it. Anything not
 * listed here surfaces in the form-level banner.
 */
const ERROR_BEARING_FIELDS: readonly string[] = [
  'phoneNumber',
  'email',
  'administrationFirstName',
  'administrationLastName',
  'companyName'
];

// Client-side mirror of the CreateCustomerDto NotBlank constraints.
const REQUIRED_MESSAGES: Record<string, string> = {
  phoneNumber: 'Phone number is required.',
  email: 'Email is required.',
  administrationFirstName: 'First name is required.',
  administrationLastName: 'Last name is required.'
};

export default function OnboardingCustomerForm({
  onCreated
}: Props): JSX.Element {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [clientErrors, setClientErrors] = useState<Record<string, string>>({});
  const createCustomer = useCreateCustomer();

  const server = toFormErrors(
    createCustomer.error,
    'Could not create this customer.',
    ERROR_BEARING_FIELDS
  );
  const errorFor = (field: string): string | undefined =>
    clientErrors[field] ?? server.fields[field];

  const update =
    (field: keyof FormState) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
      const { value } = event.target;
      setForm((prev) => ({ ...prev, [field]: value }));
    };

  const validate = (): Record<string, string> => {
    const errors: Record<string, string> = {};
    for (const [field, message] of Object.entries(REQUIRED_MESSAGES)) {
      if (form[field as keyof FormState].trim() === '') {
        errors[field] = message;
      }
    }
    return errors;
  };

  const handleSubmit = (event: React.FormEvent): void => {
    event.preventDefault();
    const errors = validate();
    setClientErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    const input: NewCustomer = {
      phoneNumber: form.phoneNumber.trim(),
      email: form.email.trim(),
      administrationFirstName: form.administrationFirstName.trim(),
      administrationLastName: form.administrationLastName.trim(),
      type: form.type
    };
    if (form.companyName.trim() !== '') {
      input.companyName = form.companyName.trim();
    }
    createCustomer.mutate(input, { onSuccess: () => onCreated?.() });
  };

  // BHP-1048: previously this only rendered when there were NO violations, so a
  // 422 naming a field this form does not show produced no field error and no
  // banner — a submit button that appeared to do nothing.
  const submitError = server.message;

  return (
    <form onSubmit={handleSubmit} noValidate className="form-auth-small">
      <TextField
        id="phoneNumber"
        label="Phone number"
        value={form.phoneNumber}
        onChange={update('phoneNumber')}
        error={errorFor('phoneNumber')}
      />
      <TextField
        id="email"
        label="Email"
        type="email"
        value={form.email}
        onChange={update('email')}
        error={errorFor('email')}
      />
      <TextField
        id="administrationFirstName"
        label="First name"
        value={form.administrationFirstName}
        onChange={update('administrationFirstName')}
        error={errorFor('administrationFirstName')}
      />
      <TextField
        id="administrationLastName"
        label="Last name"
        value={form.administrationLastName}
        onChange={update('administrationLastName')}
        error={errorFor('administrationLastName')}
      />
      <TextField
        id="companyName"
        label="Company name"
        value={form.companyName}
        onChange={update('companyName')}
        error={errorFor('companyName')}
      />

      <div className="form-group mb-3">
        <label htmlFor="type" className="form-label">
          Type
        </label>
        <select
          id="type"
          className="form-select"
          value={form.type}
          onChange={update('type')}
        >
          {CUSTOMER_TYPES.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>
      </div>

      {submitError !== null && (
        <div className="alert alert-danger py-2" role="alert">
          {submitError}
        </div>
      )}

      <button
        type="submit"
        className="btn btn-primary btn-lg btn-block w-100"
        disabled={createCustomer.isPending}
      >
        {createCustomer.isPending ? 'Creating…' : 'Create customer'}
      </button>
    </form>
  );
}

interface TextFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  error?: string;
  type?: string;
}

function TextField({
  id,
  label,
  value,
  onChange,
  error,
  type = 'text'
}: TextFieldProps): JSX.Element {
  return (
    <div className="form-group mb-3">
      <label htmlFor={id} className="form-label">
        {label}
      </label>
      <input
        id={id}
        type={type}
        className={`form-control${error !== undefined ? ' is-invalid' : ''}`}
        value={value}
        onChange={onChange}
        aria-invalid={error !== undefined}
      />
      {error !== undefined && (
        <div className="invalid-feedback d-block">{error}</div>
      )}
    </div>
  );
}
